import { Rider, Group } from '../api/rest-client';

/**
 * Skill Level filter logic for the Check-in page.
 *
 * A rider has no dedicated skill-level field. Skill level is DERIVED from the
 * rider's group: the `group_skill_level` of the group whose `name` matches
 * `rider.group` (see the Group Skill-Level feature).
 *
 * Pure functions (no React/DOM/network) — exported for property-based testing.
 *
 * Requirements: FR-2, FR-3, FR-4, FR-5, FR-7. Business rules: BR-1, BR-2, BR-8.
 * Properties: P-1 (subset), P-2 (no-op identity), P-3 (disjoint partition),
 * P-4 (membership correctness), P-5 (fail-safe).
 */

/** A derived skill level. `''` means none/unknown (blank, unmatched group, or N/A). */
export type SkillLevel = 'Beginner' | 'Intermediate' | 'Advanced' | '';

/** The concrete (non-empty) skill levels. */
export type NamedSkillLevel = Exclude<SkillLevel, ''>;

/**
 * Dropdown options, in display order (Q5=B: Advanced, Intermediate, Beginner).
 * The "All Skill Levels" (empty) option is rendered inline by the component.
 */
export const SKILL_LEVEL_OPTIONS: NamedSkillLevel[] = ['Advanced', 'Intermediate', 'Beginner'];

/** Type guard for a valid non-empty skill level. */
function isNamedSkillLevel(value: unknown): value is NamedSkillLevel {
  return value === 'Beginner' || value === 'Intermediate' || value === 'Advanced';
}

/**
 * Derive a rider's skill level from their group's `group_skill_level`.
 *
 * Fail-safe (BR-8 / SECURITY-15): never throws for malformed input. Returns
 * `''` when the rider has no group, the group is not found, or the group's
 * skill level is empty/unrecognized. Always returns one of the four allowed
 * `SkillLevel` values.
 */
export function getRiderSkillLevel(
  rider: Pick<Rider, 'group'> | null | undefined,
  groups: ReadonlyArray<Pick<Group, 'name' | 'group_skill_level'>> | null | undefined,
): SkillLevel {
  if (!rider || !Array.isArray(groups)) {
    return '';
  }
  const riderGroup = rider.group;
  if (!riderGroup) {
    return '';
  }
  const group = groups.find((g) => g != null && g.name === riderGroup);
  const level = group?.group_skill_level;
  return isNamedSkillLevel(level) ? level : '';
}

/**
 * Predicate for the Skill Level filter.
 *
 * - `selected === ''` (All Skill Levels) -> always true (no-op, keep rider).
 * - otherwise -> true only when the rider's derived skill level equals it.
 *
 * Applies uniformly to coaches and students (BR-6). Riders with a blank/
 * unmatched derived level are excluded whenever a specific level is selected
 * (BR-3).
 */
export function riderMatchesSkillLevel(
  rider: Pick<Rider, 'group'> | null | undefined,
  groups: ReadonlyArray<Pick<Group, 'name' | 'group_skill_level'>> | null | undefined,
  selected: SkillLevel,
): boolean {
  if (!selected) {
    return true;
  }
  return getRiderSkillLevel(rider, groups) === selected;
}
